import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Box, TableCell, TableRow, Typography } from "@mui/material";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { FONT_SIZE, HOLIDAY_GROUPS, INK, STATUS } from "../../../theme/tokens";
import { InHouseTooltip } from "../../InHouseTooltip/InHouseTooltip";
import { JumpingDots } from "../../UIToolkit/Animations/JumpingDots";
import { SystemRoundedCheckbox } from "../../UIToolkit/Clickable/SystemRoundedCheckbox";
import { ActionMenu, type ActionMenuAction } from "../../UIToolkit/Popups/ActionMenu";
import { CONFIG_TABLE_BODY_CELL_SX, CONFIG_TABLE_BODY_ROW_SX } from "../../UIToolkit/Tables/ConfigTable/configTableSx";
import { HolidayDayPartLabel } from "./HolidayDayPartLabel";
import { HolidayGroupSelect } from "./HolidayGroupSelect";
import { type Holiday, type HolidayGroup, type HolidayStaffCount, HolidayStatus } from "./holidayApi";
import { applyBlockedKey, formatHolidayDate, holidayStatusKey } from "./holidayFormat";
import { useSetHolidaySelectedGroup } from "./holidayQueries";

type Props = {
	holiday: Holiday;
	groups: readonly HolidayGroup[];
	canEdit: boolean;
	onEdit: (holiday: Holiday) => void;
	onDelete: (holiday: Holiday) => void;
	/** How many people Apply schedules, by job; undefined with no working group. */
	staff: HolidayStaffCell;
	/** Ticked for Apply. */
	selected: boolean;
	onSelect: (selected: boolean) => void;
};

/** The staff count: still loading, failed to load, no working group (undefined), or the counts by job. */
export type HolidayStaffCell = "loading" | "error" | HolidayStaffCount["jobs"] | undefined;

/** Text cells line up with their column header, which sits a notch further in than an in-cell control. */
const TEXT_CELL_SX = [CONFIG_TABLE_BODY_CELL_SX, { px: 1.5 }];

const STATUS_COLOR: Record<HolidayStatus, string> = {
	[HolidayStatus.NotApplied]: INK.MUTED,
	[HolidayStatus.Applied]: STATUS.SUCCESS.dark,
	[HolidayStatus.Changed]: STATUS.WARNING.main,
};

/** One holiday: its name, date and shifts, the group that works it, and where it stands. */
export const HolidayListRow: FC<Props> = ({ holiday, groups, canEdit, onEdit, onDelete, staff, selected, onSelect }) => {
	const { t } = useTranslation();
	const setGroup = useSetHolidaySelectedGroup();
	const menuItems: ActionMenuAction[] = [
		...(holiday.canEdit
			? [{ id: "edit", label: t("edit"), icon: <EditOutlinedIcon />, onSelect: () => onEdit(holiday), dataTestId: `holiday-row-edit-${holiday.id}` }]
			: []),
		...(holiday.canDelete
			? [
					{
						id: "delete",
						label: t("delete"),
						icon: <DeleteOutlineOutlinedIcon />,
						tone: "danger" as const,
						onSelect: () => onDelete(holiday),
						dataTestId: `holiday-row-delete-${holiday.id}`,
					},
				]
			: []),
	];
	const noGroupYet = holiday.selectedGroupId === null && holiday.status === HolidayStatus.NotApplied;
	const blockedKey = applyBlockedKey(holiday);

	return (
		<TableRow sx={CONFIG_TABLE_BODY_ROW_SX} data-testid={`holiday-row-${holiday.id}`}>
			{canEdit && (
				<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { width: HOLIDAY_GROUPS.LIST_SELECT_COLUMN_WIDTH_PX }]}>
					<SystemRoundedCheckbox
						checked={selected}
						onChange={onSelect}
						ariaLabel={t("holidays.apply.selectAria", { name: holiday.name })}
						disabled={blockedKey !== null}
						tooltip={blockedKey === null ? undefined : t(blockedKey)}
						data-testid={`holiday-row-select-${holiday.id}`}
					/>
				</TableCell>
			)}
			<TableCell sx={TEXT_CELL_SX}>
				<Typography sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>{holiday.name}</Typography>
			</TableCell>
			<TableCell sx={TEXT_CELL_SX}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
					<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY, whiteSpace: "nowrap" }}>{formatHolidayDate(holiday.date)}</Typography>
					<HolidayDayPartLabel holiday={holiday} />
				</Box>
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { minWidth: HOLIDAY_GROUPS.SELECT_MIN_WIDTH_PX }]}>
				<HolidayGroupSelect
					groups={groups}
					value={holiday.selectedGroupId}
					onChange={(groupId) => setGroup.mutate({ holidayId: holiday.id, groupId })}
					allowNone={false}
					emptyLabel={t("holidays.group.choose")}
					variant="table"
					label={t("holidays.list.worksAria", { name: holiday.name })}
					showLabel={false}
					// Once its hours have started, the group that works it stays.
					disabled={!canEdit || !holiday.canChangeGroup || setGroup.isPending}
					dataTestId={`holiday-row-works-${holiday.id}`}
				/>
			</TableCell>
			<TableCell sx={TEXT_CELL_SX} data-testid={`holiday-row-staff-${holiday.id}`}>
				{staff === "loading" ? (
					<JumpingDots />
				) : staff === "error" ? (
					<InHouseTooltip title={t("holidays.list.staffError")} arrow>
						<Typography component="span" sx={{ fontSize: FONT_SIZE.EMPHASIS, color: "error.main" }}>
							—
						</Typography>
					</InHouseTooltip>
				) : staff === undefined ? (
					<Typography sx={{ fontSize: FONT_SIZE.EMPHASIS, color: INK.MUTED }}>—</Typography>
				) : (
					<InHouseTooltip title={staff.length === 0 ? t("holidays.list.staffNone") : staff.map((job) => `${job.name} ${job.count}`).join(" · ")} arrow>
						<Typography component="span" sx={{ fontWeight: 600, fontSize: FONT_SIZE.EMPHASIS, color: INK.PRIMARY }}>
							{staff.reduce((total, job) => total + job.count, 0)}
						</Typography>
					</InHouseTooltip>
				)}
			</TableCell>
			<TableCell sx={TEXT_CELL_SX}>
				<Typography
					data-testid={`holiday-row-status-${holiday.id}`}
					// "Choose a group" is the one status that asks for something, so it takes the brand ink.
					sx={{ fontSize: FONT_SIZE.SECONDARY, fontWeight: 600, color: noGroupYet ? "primary.main" : STATUS_COLOR[holiday.status], whiteSpace: "nowrap" }}
				>
					{t(holidayStatusKey(holiday))}
				</Typography>
			</TableCell>
			<TableCell sx={[CONFIG_TABLE_BODY_CELL_SX, { width: 0, textAlign: "end" }]}>
				{canEdit && menuItems.length > 0 && (
					<ActionMenu items={menuItems} triggerLabel={t("holidays.list.rowActions", { name: holiday.name })} dataTestId={`holiday-row-menu-${holiday.id}`} />
				)}
			</TableCell>
		</TableRow>
	);
};
